import {
  useState,
  createContext,
  ReactNode,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useFm, useToast } from '@web3/base-hooks';
import LoginModal from '@/containers/home/Dialog/LoginModal';
import JoinedModal from '@/containers/home/Dialog/JoinedModal';
import UnsupportedCloudWallet from '@/containers/home/Dialog/UnsupportedCloudWallet';
import { LoginStatus, UserStatus } from '@/types/user';
import { useUserStatus } from '@/hooks/useUserStatus';
import { useHybirdWallet } from '@web3/base-hooks';
import { useConnectWallet } from '@/hooks';
import { NoSSR } from '@web3/base-components';
import { ErrorCode, useErrorCodes } from '@/hooks/errorCodes';
import { isApp, isMobile, newTriggerOneLink } from '@web3/base-utils';

export const RegisterContext = createContext<{
  openRegisterModal: (disabledClose?: boolean) => void;
  closeRegisterModal: (disabledClose?: boolean) => void;
  checkStatusAndRegister: (disabledClose?: boolean) => void;
}>({
  openRegisterModal: () => undefined,
  closeRegisterModal: () => undefined,
  checkStatusAndRegister: () => undefined,
});

enum ModalType {
  NONE,
  LOGIN,
  JOINED,
  CLOUD,
}

RegisterContext.displayName = 'RegisterContext';
interface Props {
  children: ReactNode;
}

export const RegisterProvider = (props: Props) => {
  const { children } = props;
  const [modalType, setModalType] = useState(ModalType.NONE);
  const fm = useFm();
  const { toastWarn } = useToast();
  // 个人页如果不满足条件不能让用户手动关闭弹窗，所以加了个属性控制
  const [disabledClose, setDisabledClose] = useState(false);
  const { openSpotLoginModal } = useHybirdWallet();
  const { connectWallet } = useConnectWallet();
  const { toastSuccess, toastFail } = useToast();
  const { queryErrorMsg } = useErrorCodes();

  const {
    loginStatus,
    userStatus,
    isBindingLoading,
    isRegisterLoading,
    beginBinding,
    beginRegister,
  } = useUserStatus();
  const enableAutoBind = useRef(false);
  const hasReqRegister = useRef(false);
  const hasReqLogin = useRef(false);
  const toBindAccountType = useRef<'WALLET' | 'SPOT'>(); // 这个变量是为了绑定成功弹出的toast

  const connectWalletAndClose = async () => {
    await connectWallet();
  };

  const connectBybitAccountAndClose = async () => {
    await openSpotLoginModal();
  };

  const bindingAndRespond = async () => {
    if (isBindingLoading) {
      return;
    }
    const result = await beginBinding();
    if (result) {
      toastSuccess(
        toBindAccountType.current === 'WALLET'
          ? fm('web3WalletLinkedCapIncreased')
          : fm('bybitAccountLinkedPointsCapIncreased')
      );
      setModalType(ModalType.NONE);
      toBindAccountType.current = null;
    } else {
      toastFail(queryErrorMsg(ErrorCode.WSOT_USER_BOUND));
    }
  };

  const bindAndClose = async () => {
    switch (loginStatus) {
      case LoginStatus.BOTH:
        toBindAccountType.current = 'SPOT'; // 默认以Wallet为主，绑定spot
        await bindingAndRespond();
        setModalType(ModalType.NONE);
        break;
      case LoginStatus.ONLY_WALLET:
        toBindAccountType.current = 'SPOT';
        connectBybitAccountAndClose();
        enableAutoBind.current = true;
        break;
      case LoginStatus.ONLY_SPOT:
        toBindAccountType.current = 'WALLET';
        connectWalletAndClose();
        enableAutoBind.current = true;
        break;
    }
  };

  useEffect(() => {
    if (
      enableAutoBind.current &&
      loginStatus === LoginStatus.BOTH &&
      userStatus !== UserStatus.SPOT_FINISH &&
      userStatus !== UserStatus.WALLET_FINISH // 只要助记词钱包未绑定，都尝试绑定
    ) {
      bindingAndRespond();
      enableAutoBind.current = false;
    }
  }, [loginStatus, userStatus]);

  const openRegisterModal = useCallback(
    (disabledClose?: boolean) => {
      if (isMobile && !isApp()) {
        newTriggerOneLink(window.location.href);
        return;
      }
      if (disabledClose) {
        setDisabledClose(true);
      }

      if (loginStatus === LoginStatus.NOT_TARGET_TYPE) {
        setModalType(ModalType.CLOUD);
        return;
      }

      if ([UserStatus.NOT_READY].includes(userStatus)) {
        hasReqLogin.current = true;
        setModalType(ModalType.LOGIN);
        return;
      }

      if (
        [UserStatus.SPOT_NOT_BINDING, UserStatus.WALLET_NOT_BINDING].includes(
          userStatus
        )
      ) {
        setModalType(ModalType.JOINED);
      }
    },
    [userStatus, loginStatus]
  );

  const closeRegisterModal = () => {
    setModalType(ModalType.NONE);
  };

  const beginRegisterAndRecord = useCallback(async () => {
    // 这些判断是为了避免loginStatus或者userStatus因异步的原因未扭转过来就调用了该方法
    if (
      [
        LoginStatus.NONE,
        LoginStatus.NOT_READY,
        LoginStatus.NOT_TARGET_TYPE,
      ].includes(loginStatus)
    ) {
      openRegisterModal();
      return false;
    }
    if (userStatus === UserStatus.NOT_READY) {
      return false;
    }
    if (
      [
        UserStatus.WALLET_FINISH,
        UserStatus.SPOT_FINISH,
        UserStatus.SPOT_NOT_BINDING,
        UserStatus.WALLET_NOT_BINDING,
      ].includes(userStatus)
    ) {
      toastSuccess(fm('connectSuccessfully'));
      return true;
    }

    // const result = await beginRegister();
    // if (result) {
    //   hasReqRegister.current = true;
    // }
    // toastFail(fm('yetToRegister'));
    return true;
  }, [loginStatus, userStatus]);

  useEffect(() => {
    async function register() {
      if (hasReqLogin.current && !isRegisterLoading) {
        const result = await beginRegisterAndRecord();
        if (result) {
          hasReqLogin.current = false;
          setModalType(ModalType.NONE);
        }
      }
    }
    register();
  }, [beginRegisterAndRecord, isRegisterLoading]);

  const checkStatusAndRegister = async (disabledClose?: boolean) => {
    if (isMobile && !isApp()) {
      newTriggerOneLink(window.location.href);
      return;
    }
    if (
      [
        LoginStatus.NONE,
        LoginStatus.NOT_READY,
        LoginStatus.NOT_TARGET_TYPE,
      ].includes(loginStatus)
    ) {
      openRegisterModal(disabledClose);
      return;
    }
    if (
      loginStatus === LoginStatus.ONLY_SPOT ||
      loginStatus === LoginStatus.ONLY_WALLET ||
      loginStatus === LoginStatus.BOTH
    ) {
      beginRegisterAndRecord();
    }
  };

  const handleUnsupportedCloudWallet = () => {
    connectWalletAndClose();
    hasReqLogin.current = true;
  };

  useEffect(() => {
    if (
      hasReqRegister.current &&
      (userStatus === UserStatus.SPOT_NOT_BINDING ||
        userStatus === UserStatus.WALLET_NOT_BINDING)
    ) {
      openRegisterModal(disabledClose);
      hasReqRegister.current = false;
    }
  }, [userStatus, openRegisterModal, disabledClose]);

  return (
    <RegisterContext.Provider
      value={{
        openRegisterModal,
        closeRegisterModal,
        checkStatusAndRegister,
      }}
    >
      {children}
      <NoSSR>
        <LoginModal
          visible={modalType === ModalType.LOGIN}
          isLoading={isRegisterLoading}
          onClose={() => {
            if (disabledClose && [UserStatus.NOT_READY].includes(userStatus)) {
              return;
            }
            setModalType(ModalType.NONE);
          }}
          onConnectBybitAccount={connectBybitAccountAndClose}
          onConnectWeb3Wallet={connectWalletAndClose}
        />
        <JoinedModal
          visible={modalType === ModalType.JOINED}
          isLoading={isBindingLoading}
          connectedType={
            userStatus === UserStatus.WALLET_NOT_BINDING ? 'SPOT' : 'WALLET'
          }
          onClose={() => {
            setModalType(ModalType.NONE);
          }}
          mainText={
            userStatus === UserStatus.WALLET_NOT_BINDING
              ? fm('bybitAccountLinkedPointsCapIncreased')
              : fm('web3WalletLinkedCapIncreased')
          }
          bonusText={
            userStatus === UserStatus.WALLET_NOT_BINDING
              ? fm('connectBybitWeb3WalletDoublePointsLimit')
              : fm('connectBybitMainAccountTip')
          }
          confirmBtnText={
            userStatus === UserStatus.WALLET_NOT_BINDING
              ? fm('aidolConnectWeb3Wallet')
              : fm('aidolConnectBybitAccount')
          }
          onConfirm={bindAndClose}
        />
        <UnsupportedCloudWallet
          visible={modalType === ModalType.CLOUD}
          onClose={() => {
            if (disabledClose && [UserStatus.NOT_READY].includes(userStatus)) {
              return;
            }
            setModalType(ModalType.NONE);
          }}
          onConfirm={handleUnsupportedCloudWallet}
        />
      </NoSSR>
    </RegisterContext.Provider>
  );
};
